/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { toast } from "sonner"

import type { GroupedSessionsByDate, UseChatHistoryProps } from "./use-chat-history.types"
import type { ChatAppResponse, ChatSessionModel } from "@/api/models"

import { FactCheckJobManager } from "@/features/chat/utils/fact-check-job-manager"
import { filterSessionFileRefs } from "@/features/chat/utils/session-file-refs"
import { useAgentState } from "@/hooks/use-agent-state"
import { useSessionId } from "@/hooks/use-session-id"
import { useSessionMessages } from "@/hooks/use-session-messages"
import { useSessionSync } from "@/hooks/use-session-sync"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { useNavigatorStore } from "@/stores/navigator-store"
import { defaultSessionState } from "@/stores/slices/chat-slice"

import {
  disposeChatRequestSession,
  getChatRequestSessionDisposalGeneration,
  isChatRequestSessionDisposed,
} from "./chat-request-registry"
import {
  addMessageId,
  ensureActivitiesArray,
  isVoiceTranscriptContent,
  processVoiceTranscriptMessage,
  removeChatSession,
  removeChatSessions,
} from "./use-chat-history-helpers"
import { useChatSessionSyncPublisher } from "./use-chat-session-cross-tab-sync"
import { useChatSessionQueryScope } from "./use-chat-session-query-scope"
import { useFavoriteSessionsSync } from "./use-favorite-sessions-sync"
import { useRenameChatSession } from "./use-rename-chat-session"
import { useSessionsQuery } from "./use-sessions-query"

export type { UseChatHistoryProps } from "./use-chat-history.types"
export function useChatHistory({
  sessionType,
  useChatStore,
  projectId,
  externalSearchTerm,
  syncRoute = true,
}: UseChatHistoryProps) {
  const [chatService] = useState<ChatService>(ChatService.getInstance())
  const [currentDropdownChatSession, setCurrentDropdownChatSession] =
    useState<ChatSessionModel | null>(null)
  const [selectedSessions, setSelectedSessions] = useState<ChatSessionModel[]>([])
  const [showDeleteSessionAlertDialog, setShowDeleteSessionAlertDialog] = useState(false)
  const [showDeleteSessionsAlertDialog, setShowDeleteSessionsAlertDialog] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectMode, setSelectMode] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(undefined)
  // Bumped on every real selection so the hydration effect re-runs even when
  // `selectedSessionId` is unchanged: this instance can be re-asked for the
  // session it hydrated last after another instance (Explorer's sidebar) or a
  // create-on-send moved the store to a different chat in between (#3515).
  const [hydrationRequest, setHydrationRequest] = useState(0)
  const selectedSessionIdRef = useRef<string | undefined>(undefined)
  // Set when this instance is asked again for the session it loaded last:
  // hydration then waits for a refetch that started after `since` instead of
  // settling on cached rows (Explorer restores Deep mode once, from the rows
  // present when isHydrating clears).
  const freshnessBarrierRef = useRef<{ sessionId: string; since: number } | null>(null)
  // Deletion is tracked only by the store-scoped request registry, so every
  // instance on the store agrees (#3524). A selection records the session's
  // disposal generation: if the session is deleted afterwards, this selection
  // can never hydrate it again, even once a new request revives the id.
  const selectionFenceRef = useRef({ disposalGeneration: 0 })

  const effectiveSearchTerm = externalSearchTerm || searchTerm

  const { t } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()
  // Cross-window sync (issue #2548). Both delete mutations below invalidate
  // the UNSCOPED `sessionHistory(sessionType)` key, so the hint they publish
  // carries the same unscoped scope rather than this hook's `projectId`.
  const publishSessionChange = useChatSessionSyncPublisher({ sessionType })
  const {
    renamingSessionId,
    startRenamingSession,
    cancelRenamingSession,
    handleRenameSessionTitle,
    renameChatSessionMutation,
  } = useRenameChatSession({
    sessionType,
    chatService,
    setSelectedSessions,
    setCurrentDropdownChatSession,
    selectMode,
  })

  const { data: sessions = [], isPending: isLoadingSessions } = useSessionsQuery(
    sessionType,
    projectId
  )

  const currentSessionId = useSessionId()
  const chatStore = useChatStore()
  const setSessions = chatStore((state) => state.setSessions)
  const changeChatState = chatStore((state) => state.changeChatState)
  const resetChatState = chatStore((state) => state.resetChatState)
  const setCurrentSessionId = chatStore((state) => state.setCurrentSessionId)
  const resetFiles = chatStore((state) => state.resetFiles)
  const setSessionFileRefs = chatStore((state) => state.setSessionFileRefs)
  const navigatorStore = useNavigatorStore(sessionType)
  const setCitations = navigatorStore((state) => state.setCitations)
  const lastHydratedSessionIdRef = useRef<string | undefined>(undefined)

  const { toggleFavoriteSession, isFavorited, getFavoriteSessions } =
    useFavoriteSessionsSync(sessionType)

  const deleteChatSessionsMutation = useMutation({
    mutationKey: queryKeys.deleteChatSessions,
    mutationFn: (sessionIds: string[]) => removeChatSessions(sessionIds),
    onSuccess: async (_response, sessionIds) => {
      const isActiveSession = sessionIds.some(
        (sessionId) => sessionId === chatService.getOpenSession(sessionType)?.id
      )

      for (const sessionId of sessionIds) {
        disposeChatRequestSession(chatStore, sessionId)
        chatStore.getState().removeSessionState(sessionId)
      }
      if (selectedSessionIdRef.current && sessionIds.includes(selectedSessionIdRef.current)) {
        selectedSessionIdRef.current = undefined
        setSelectedSessionId(undefined)
      }
      chatService.removeSessions(sessionIds)

      // Invalidate sessions query instead of manual fetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
      publishSessionChange("deleted")
      setSelectedSessions([])
      setSelectMode(false) // Exit select mode after successful deletion
      if (isActiveSession) {
        clearChat()
        setCitations([])
      }
      setShowDeleteSessionsAlertDialog(false)

      // Note: Favorites are automatically removed from database when toggling

      toast.success(t("chatsession.deleted_many"))
    },
  })

  const deleteChatSessionMutation = useMutation({
    mutationKey: queryKeys.deleteChatSession,
    mutationFn: (sessionId: string) => removeChatSession(sessionId),
    onSuccess: async (_response, sessionId) => {
      const isActiveSession = sessionId === chatService.getOpenSession(sessionType)?.id

      if (selectedSessionIdRef.current === sessionId) {
        selectedSessionIdRef.current = undefined
        setSelectedSessionId(undefined)
      }
      disposeChatRequestSession(chatStore, sessionId)
      chatStore.getState().removeSessionState(sessionId)
      chatService.removeSession(sessionId)

      // Invalidate sessions query instead of manual fetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
      publishSessionChange("deleted")
      if (isActiveSession) {
        clearChat()
        setCitations([])
      }

      // Note: We don't auto-unfavorite when deleting, user can manage favorites separately

      setShowDeleteSessionAlertDialog(false)
      toast.success(t("chatsession.deleted"))
    },
  })

  // Sync React Query sessions data to Zustand store for backward compatibility
  useEffect(() => {
    if (sessions.length > 0) {
      setSessions(sessions)

      // Also sync to ChatService for components that read from it (e.g., download filename)
      const openSession = chatService.getOpenSession(sessionType)
      if (openSession?.id) {
        const updatedSession = sessions.find((s) => s?.id === openSession.id)
        if (updatedSession?.title && updatedSession.title !== openSession.title) {
          chatService.updateSessionTitle(openSession.id, updatedSession.title)
        }
      }
    }
  }, [sessions, setSessions, chatService, sessionType])

  const sessionsWithTitle = useMemo(() => sessions.filter((session) => session?.title), [sessions])
  const filteredSessions = useMemo(() => {
    if (!effectiveSearchTerm) {
      return sessionsWithTitle
    }
    return sessionsWithTitle.filter((session) =>
      session?.title?.toLowerCase().includes(effectiveSearchTerm.toLowerCase())
    )
  }, [sessionsWithTitle, effectiveSearchTerm])

  // Get favorite sessions from database
  const favoriteSessions = useMemo(
    () => getFavoriteSessions(sessions),
    [getFavoriteSessions, sessions]
  )

  // Hydrate favorites with latest data from sessions (to include fresh iconType/lastFunctionUsed)
  const hydratedFavoriteSessions = useMemo(() => {
    const normalize = (value?: string) =>
      value?.toString().trim().toLowerCase().replaceAll("-", "_")

    return favoriteSessions.map((fav) => {
      if (!fav?.id) return fav

      // Soft-normalize icon metadata so the UI resolver has consistent inputs
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const m: any = { ...fav }
      if (m.lastFunctionUsed) m.lastFunctionUsed = normalize(m.lastFunctionUsed)
      if (m.iconType) m.iconType = normalize(m.iconType)
      return m
    })
  }, [favoriteSessions])

  const filteredFavoriteSessions = useMemo(() => {
    if (!effectiveSearchTerm) {
      return hydratedFavoriteSessions
    }
    return hydratedFavoriteSessions.filter((session) =>
      session?.title?.toLowerCase().includes(effectiveSearchTerm.toLowerCase())
    )
  }, [hydratedFavoriteSessions, effectiveSearchTerm])
  const groupedFilteredSessionsByDate = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(today.getDate() - 7)

    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(today.getDate() - 30)

    const groups = filteredSessions.reduce(
      (acc, session) => {
        if (!session?.created || isFavorited(session.id)) {
          return acc
        }

        const sessionDate = new Date(session.created)
        sessionDate.setHours(0, 0, 0, 0)

        if (sessionDate.getTime() === today.getTime()) {
          acc.today.sessions.push(session)
        } else if (sessionDate >= sevenDaysAgo) {
          acc.past7Days.sessions.push(session)
        } else if (sessionDate >= thirtyDaysAgo) {
          acc.past30Days.sessions.push(session)
        } else {
          acc.allTime.sessions.push(session)
        }

        return acc
      },
      {
        today: { title: t("chatsession.today"), sessions: [] } as GroupedSessionsByDate,
        past7Days: { title: t("chatsession.past_7_days"), sessions: [] } as GroupedSessionsByDate,
        past30Days: { title: t("chatsession.past_30_days"), sessions: [] } as GroupedSessionsByDate,
        allTime: { title: t("chatsession.all_time"), sessions: [] } as GroupedSessionsByDate,
      }
    )

    return Object.fromEntries(
      Object.entries(groups).filter(([, group]) => group.sessions.length > 0)
    )
  }, [filteredSessions, t, isFavorited])

  const clearChat = () => {
    resetChatState()
    chatService.closeSession(sessionType)
    setCurrentSessionId(null)
    resetFiles()
  }

  const deleteChatSession = async () => {
    if (!currentDropdownChatSession?.id) {
      return
    }

    await deleteChatSessionMutation.mutateAsync(currentDropdownChatSession.id)
  }

  const deleteChatSessions = async () => {
    const sessionIds = selectedSessions
      .map((session) => session?.id)
      .filter((id) => id !== undefined) as string[]

    await deleteChatSessionsMutation.mutateAsync(sessionIds)
  }

  const handleDropdownMenuOpen = (isOpen: boolean, chat: ChatSessionModel) => {
    if (chat?.id !== currentDropdownChatSession?.id && isOpen) {
      setCurrentDropdownChatSession(chat)
    }
  }

  // The query identity this render reads: a deleted chat fetches nothing, and a
  // revived one reads keys no pre-deletion fetch used (#3524).
  const queryScope = useChatSessionQueryScope(chatStore, selectedSessionId)
  const messageCacheBuster = useMemo(
    () => new URLSearchParams(location.search).get("factCheckUpdatedAt") ?? undefined,
    [location.search]
  )
  const {
    data: messages,
    isFetched: isMessagesFetched,
    dataUpdatedAt: messagesUpdatedAt,
    errorUpdatedAt: messagesErrorUpdatedAt,
  } = useSessionMessages(selectedSessionId, messageCacheBuster, queryScope)
  const applyFactCheckResultOverride = useCallback(
    (response: ChatAppResponse): ChatAppResponse => {
      const messageId = response.messageId ?? response.context.message_id
      if (!messageId) {
        return response
      }

      const factCheckJobManager = FactCheckJobManager.getInstance()
      const cachedResult = factCheckJobManager.getCompletedResult({
        sessionType,
        sessionId: selectedSessionId,
        messageId,
      })

      if (response.context.fact_check_result) {
        if (cachedResult) {
          factCheckJobManager.clearCompletedResult({
            sessionType,
            sessionId: selectedSessionId,
            messageId,
          })
        }
        return response
      }

      if (!cachedResult) {
        return response
      }

      return {
        ...response,
        context: {
          ...response.context,
          fact_check_result: cachedResult as unknown as Record<string, unknown>,
        } as ChatAppResponse["context"],
      }
    },
    [selectedSessionId, sessionType]
  )
  const {
    data: agentStateResponse,
    isEnabled: isAgentStateEnabled,
    dataUpdatedAt: agentStateUpdatedAt,
    errorUpdatedAt: agentStateErrorUpdatedAt,
  } = useAgentState(selectedSessionId, sessionType, queryScope)
  useEffect(() => {
    // The registry tombstone and disposal generation are store-scoped, so they
    // also cover deletes made by another useChatHistory instance (Explorer
    // mounts one at page level and one in the sidebar). Read them again here,
    // synchronously: a delete or revive can land after this render. Hydrate
    // only when the selection and the query data this render holds both
    // belong to the chat's current generation (#3524).
    const currentGeneration = selectedSessionId
      ? getChatRequestSessionDisposalGeneration(chatStore, selectedSessionId)
      : 0
    if (
      !selectedSessionId ||
      isChatRequestSessionDisposed(chatStore, selectedSessionId) ||
      queryScope.disposed ||
      currentGeneration !== selectionFenceRef.current.disposalGeneration ||
      currentGeneration !== queryScope.generation
    ) {
      return
    }

    // Wait until messages have been fetched (could be empty array)
    if (!isMessagesFetched) {
      return
    }

    const barrier = freshnessBarrierRef.current
    if (barrier?.sessionId === selectedSessionId) {
      // A failed refetch also settles: hydrate from the cached rows then.
      const settledSince = (dataAt: number, errorAt: number) =>
        Math.max(dataAt, errorAt) >= barrier.since
      if (
        !settledSince(messagesUpdatedAt, messagesErrorUpdatedAt) ||
        (isAgentStateEnabled && !settledSince(agentStateUpdatedAt, agentStateErrorUpdatedAt))
      ) {
        return
      }
      freshnessBarrierRef.current = null
    }

    // Handle case where session has no messages
    if (!messages || (Array.isArray(messages) && messages.length === 0)) {
      chatStore.getState().updateSession(selectedSessionId, { isHydrating: false })
      lastHydratedSessionIdRef.current = selectedSessionId
      return
    }

    const processMessages = () => {
      const typedMessages = messages as Array<{ id: string; content: unknown }>
      const answers = typedMessages.reduce<[string, ChatAppResponse][]>(
        (acc, message) => {
          try {
            const content = message.content as unknown as
              | [string, ChatAppResponse]
              | [string, ChatAppResponse][]

            // Check for voice transcript (multi-turn realtime audio format)
            // Each turn becomes a separate message pair for normal chat rendering
            if (isVoiceTranscriptContent(content)) {
              const voiceMessages = processVoiceTranscriptMessage(
                content as [string, { content?: string; source?: string }][],
                message.id
              )
              return acc.concat(voiceMessages)
            }

            // Regular message format
            if (Array.isArray(content)) {
              if (content.length > 0) {
                if (
                  content.length === 2 &&
                  typeof content[0] === "string" &&
                  typeof content[1] === "object"
                ) {
                  const responseObj = content[1] as ChatAppResponse
                  const responseWithId = addMessageId(responseObj, message.id)
                  const responseWithActivities = ensureActivitiesArray(responseWithId)
                  return acc.concat([
                    [content[0], applyFactCheckResultOverride(responseWithActivities)],
                  ])
                } else {
                  const processedContent = content.map((item) => {
                    if (Array.isArray(item)) {
                      const responseWithId = addMessageId(item[1] as ChatAppResponse, message.id)
                      const responseWithActivities = ensureActivitiesArray(responseWithId)
                      return [item[0], applyFactCheckResultOverride(responseWithActivities)]
                    }
                    return item
                  })
                  return acc.concat(processedContent as [string, ChatAppResponse][])
                }
              }
            } else if (typeof content === "object") {
              return acc
            } else {
              return acc
            }

            return acc.concat(Array.isArray(content) ? content : [content])
          } catch (error) {
            logger.error("Error processing message content", error as Error)
            return acc
          }
        },
        [] as [string, ChatAppResponse][]
      )

      // Add agent state to latest answer if available (for Explorer sessions)
      const agentState = agentStateResponse?.agent_state
      if (agentState && answers.length > 0) {
        const latestAnswer = answers[answers.length - 1]
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (latestAnswer && latestAnswer[1]) {
          const latestResponse = latestAnswer[1]

          // Add agent state to context
          latestResponse.context.agent_state = agentState
        }
      }

      const lastQuestionMessage = answers.length > 0 ? answers[answers.length - 1][0] : ""

      // Initialize session state if needed
      const store = chatStore.getState()
      if (!(selectedSessionId in store.sessionStates)) {
        store.initSession(selectedSessionId)
      }

      // Merge with current in-memory state
      const sessionState = store.sessionStates[selectedSessionId] ?? { ...defaultSessionState }
      const currentMessages = sessionState.messages

      // MessageId-based merge strategy:
      // Preserve all server messages + any client messages not yet in server
      // This handles the case where message_saved arrives but server hasn't persisted to Cosmos yet

      const reservedServerIndexes = new Set<number>()
      const exactlyMatchedClientIndexes = new Set<number>()
      const matchedServerIndexByClientIndex = new Map<number, number>()
      const serverMessageIds = new Set(
        answers
          .map(([, response]) => response.messageId ?? response.context.message_id)
          .filter(Boolean)
      )
      const serverTurnIds = new Set(answers.map(([, response]) => response.turn_id).filter(Boolean))
      const preferredClientByTurnId = new Map<
        string,
        {
          representativeClientIndex: number
          placementClientIndex: number
          preference: number
        }
      >()

      // Canonicalize duplicate local copies independently of transcript order.
      // Exact server identity wins, followed by a local message ID, followed by a
      // completed no-ID row. The preferred payload keeps the earliest occurrence's
      // logical position so replacing a stale copy cannot reorder later turns.
      for (const [clientIndex, [, clientMsg]] of currentMessages.entries()) {
        const turnId = clientMsg.turn_id
        if (!turnId) continue

        const clientMsgId = clientMsg.messageId ?? clientMsg.context.message_id
        const hasExactServerIdentity =
          (Boolean(clientMsgId) && serverMessageIds.has(clientMsgId)) || serverTurnIds.has(turnId)
        const preference =
          (hasExactServerIdentity ? 4 : 0) +
          (clientMsgId ? 2 : 0) +
          (!clientMsgId && clientMsg.completed ? 1 : 0)
        const preferredClient = preferredClientByTurnId.get(turnId)

        if (!preferredClient) {
          preferredClientByTurnId.set(turnId, {
            representativeClientIndex: clientIndex,
            placementClientIndex: clientIndex,
            preference,
          })
        } else if (preference > preferredClient.preference) {
          preferredClientByTurnId.set(turnId, {
            ...preferredClient,
            representativeClientIndex: clientIndex,
            preference,
          })
        }
      }

      // Reserve every exact message-ID occurrence one-to-one before allowing any
      // content fallback. IDs take precedence over client-only turn IDs.
      for (const [clientIndex, [, clientMsg]] of currentMessages.entries()) {
        if (
          clientMsg.turn_id &&
          preferredClientByTurnId.get(clientMsg.turn_id)?.representativeClientIndex !== clientIndex
        ) {
          continue
        }

        const clientMsgId = clientMsg.messageId ?? clientMsg.context.message_id
        if (!clientMsgId) continue

        const matchingServerIndex = answers.findIndex(
          ([, serverMsg], serverIndex) =>
            !reservedServerIndexes.has(serverIndex) &&
            (serverMsg.messageId ?? serverMsg.context.message_id) === clientMsgId
        )
        if (matchingServerIndex < 0) continue

        reservedServerIndexes.add(matchingServerIndex)
        exactlyMatchedClientIndexes.add(clientIndex)
        const placementClientIndex = clientMsg.turn_id
          ? (preferredClientByTurnId.get(clientMsg.turn_id)?.placementClientIndex ?? clientIndex)
          : clientIndex
        matchedServerIndexByClientIndex.set(placementClientIndex, matchingServerIndex)
      }

      // Then reserve stable turn identities globally. Doing this in a separate
      // pass prevents an earlier identical row from consuming the server row that
      // canonically belongs to a later turn.
      for (const [clientIndex, [, clientMsg]] of currentMessages.entries()) {
        if (exactlyMatchedClientIndexes.has(clientIndex) || !clientMsg.turn_id) continue
        if (
          preferredClientByTurnId.get(clientMsg.turn_id)?.representativeClientIndex !== clientIndex
        ) {
          continue
        }

        const matchingServerIndex = answers.findIndex(
          ([, serverMsg], serverIndex) =>
            !reservedServerIndexes.has(serverIndex) && serverMsg.turn_id === clientMsg.turn_id
        )
        if (matchingServerIndex < 0) continue

        reservedServerIndexes.add(matchingServerIndex)
        exactlyMatchedClientIndexes.add(clientIndex)
        const placementClientIndex =
          preferredClientByTurnId.get(clientMsg.turn_id)?.placementClientIndex ?? clientIndex
        matchedServerIndexByClientIndex.set(placementClientIndex, matchingServerIndex)
      }

      const preservedClientMessages: Array<{
        clientIndex: number
        message: [string, ChatAppResponse]
      }> = []

      // Identify client messages that need preserving. A stream can settle before
      // message_saved supplies an ID, so retain that completed local turn as well;
      // incomplete/no-turn rows are transient placeholders.
      for (const [clientIndex, [question, clientMsg]] of currentMessages.entries()) {
        const clientMsgId = clientMsg.messageId ?? clientMsg.context.message_id
        const isSettledLocalTurn = !clientMsgId && clientMsg.completed && Boolean(clientMsg.turn_id)

        if (exactlyMatchedClientIndexes.has(clientIndex)) continue
        if (
          clientMsg.turn_id &&
          preferredClientByTurnId.get(clientMsg.turn_id)?.representativeClientIndex !== clientIndex
        ) {
          continue
        }

        if (clientMsgId) {
          // Client has a message server doesn't have yet - preserve it
          const placementClientIndex = clientMsg.turn_id
            ? (preferredClientByTurnId.get(clientMsg.turn_id)?.placementClientIndex ?? clientIndex)
            : clientIndex
          preservedClientMessages.push({
            clientIndex: placementClientIndex,
            message: [question, clientMsg],
          })
          continue
        }

        if (!isSettledLocalTurn) continue

        const localTurnId = clientMsg.turn_id
        if (!localTurnId) continue

        const matchingContentIndex = answers.findIndex(
          ([serverQuestion, serverMsg], index) =>
            !reservedServerIndexes.has(index) &&
            serverQuestion === question &&
            serverMsg.content === clientMsg.content &&
            (!serverMsg.turn_id || serverMsg.turn_id === localTurnId)
        )
        if (matchingContentIndex >= 0) {
          reservedServerIndexes.add(matchingContentIndex)
          const placementClientIndex =
            preferredClientByTurnId.get(localTurnId)?.placementClientIndex ?? clientIndex
          matchedServerIndexByClientIndex.set(placementClientIndex, matchingContentIndex)
          continue
        }

        const placementClientIndex =
          preferredClientByTurnId.get(localTurnId)?.placementClientIndex ?? clientIndex
        preservedClientMessages.push({
          clientIndex: placementClientIndex,
          message: [question, clientMsg],
        })
      }

      preservedClientMessages.sort((left, right) => left.clientIndex - right.clientIndex)

      // Keep canonical server order. A local row can precede its next server
      // anchor only when that anchor is ahead of every prior anchor. If the next
      // anchor moves backward, place the local row after the furthest seen anchor.
      const greatestServerIndexBeforeClient: number[] = []
      let greatestSeenServerIndex = -1
      for (const clientIndex of currentMessages.keys()) {
        greatestServerIndexBeforeClient[clientIndex] = greatestSeenServerIndex
        const matchedServerIndex = matchedServerIndexByClientIndex.get(clientIndex)
        if (matchedServerIndex !== undefined) {
          greatestSeenServerIndex = Math.max(greatestSeenServerIndex, matchedServerIndex)
        }
      }

      const clientMessagesByServerBoundary = new Map<number, Array<[string, ChatAppResponse]>>()

      for (const { clientIndex, message } of preservedClientMessages) {
        let nextServerIndex: number | undefined
        for (
          let nextClientIndex = clientIndex + 1;
          nextClientIndex < currentMessages.length;
          ++nextClientIndex
        ) {
          const matchedServerIndex = matchedServerIndexByClientIndex.get(nextClientIndex)
          if (matchedServerIndex !== undefined) {
            nextServerIndex = matchedServerIndex
            break
          }
        }

        const greatestPriorServerIndex = greatestServerIndexBeforeClient[clientIndex] ?? -1
        let serverBoundary = answers.length
        if (nextServerIndex !== undefined) {
          serverBoundary =
            nextServerIndex > greatestPriorServerIndex
              ? nextServerIndex
              : Math.min(greatestPriorServerIndex + 1, answers.length)
        }

        const messagesAtBoundary = clientMessagesByServerBoundary.get(serverBoundary) ?? []
        messagesAtBoundary.push(message)
        clientMessagesByServerBoundary.set(serverBoundary, messagesAtBoundary)
      }

      const mergedMessages: Array<[string, ChatAppResponse]> = []
      for (const [serverIndex, serverMessage] of answers.entries()) {
        mergedMessages.push(...(clientMessagesByServerBoundary.get(serverIndex) ?? []))
        mergedMessages.push(serverMessage)
      }
      mergedMessages.push(...(clientMessagesByServerBoundary.get(answers.length) ?? []))

      // Update session state with merged messages and clear hydration state
      chatStore.getState().updateSession(selectedSessionId, {
        messages: mergedMessages,
        // Only seed `lastQuestion` from the server when the window has none.
        // Retry and Continue resend it, and this effect re-runs on cached
        // (possibly stale) rows whenever a dependency changes; overwriting a
        // live value would make Retry resend an older question (#3322 B3).
        lastQuestion: sessionState.lastQuestion[0]
          ? sessionState.lastQuestion
          : [lastQuestionMessage, []],
        isHydrating: false,
      })

      lastHydratedSessionIdRef.current = selectedSessionId
    }

    void processMessages()
  }, [
    messages,
    isMessagesFetched,
    messagesUpdatedAt,
    messagesErrorUpdatedAt,
    agentStateResponse,
    isAgentStateEnabled,
    agentStateUpdatedAt,
    agentStateErrorUpdatedAt,
    selectedSessionId,
    sessionType,
    applyFactCheckResultOverride,
    chatService,
    sessions,
    changeChatState,
    chatStore,
    hydrationRequest,
    queryScope,
  ])

  const handleSelectSession = useCallback(
    (session: ChatSessionModel) => {
      // Selection and route sync never revive a deleted session; only a new
      // request (beginChatRequest) clears the registry tombstone.
      if (!session?.id || isChatRequestSessionDisposed(chatStore, session.id)) {
        return
      }

      // Already the active store session (useSessionSync re-notifies when
      // ChatService still owns it after feature navigation). Only re-apply
      // selection so response-unread clears without resetting hydration/files.
      // A session still hydrating whose load this instance didn't start lost
      // its owner mid-fetch (page remount), so adopt it instead (#3515).
      const state = chatStore.getState()
      const isStillHydrating =
        session.id in state.sessionStates && state.sessionStates[session.id].isHydrating
      if (
        state.currentSessionId === session.id &&
        (!isStillHydrating || selectedSessionIdRef.current === session.id)
      ) {
        setCurrentSessionId(session.id)
        return
      }

      // Reset citations, activeCitations, and files when loading a new session
      // This ensures clean state regardless of how the session is loaded
      // (sidebar click, URL navigation, projects view, etc.)
      navigatorStore.getState().setCitations([])
      navigatorStore.getState().setActiveCitations([])
      resetFiles()

      // Load persisted file references from the session (filenames only)
      const fileRefs = filterSessionFileRefs(session.file_refs ?? [])
      setSessionFileRefs(fileRefs)

      // Initialize session with isHydrating: true to prevent home screen flash during switch
      const store = chatStore.getState()
      if (!(session.id in store.sessionStates)) {
        store.initSession(session.id)
      }
      store.updateSession(session.id, { isHydrating: true })

      // Back to the session this instance loaded last: its query keeps the
      // same key, so refetch and hydrate from the fresh rows (#3515).
      if (selectedSessionIdRef.current === session.id) {
        freshnessBarrierRef.current = { sessionId: session.id, since: Date.now() }
        void queryClient.invalidateQueries({ queryKey: queryKeys.sessionMessages(session.id) })
        void queryClient.invalidateQueries({ queryKey: queryKeys.agentState(session.id) })
      } else {
        freshnessBarrierRef.current = null
      }
      // A revived chat needs no special refetch: its queries use keys that no
      // fetch for the deleted chat ever wrote to (#3524).
      selectionFenceRef.current = {
        disposalGeneration: getChatRequestSessionDisposalGeneration(chatStore, session.id),
      }

      setCurrentSessionId(session.id)
      selectedSessionIdRef.current = session.id
      setSelectedSessionId(session.id) // Trigger React Query fetch
      setHydrationRequest((request) => request + 1)
    },
    [chatStore, queryClient, resetFiles, navigatorStore, setCurrentSessionId, setSessionFileRefs]
  )

  const handleClearSession = useCallback(() => {
    setCurrentSessionId(null)
  }, [setCurrentSessionId])

  // Automatically sync URL sessionId with ChatService open session
  useSessionSync(sessionType, sessions, {
    onSelectSession: handleSelectSession,
    onClearSession: handleClearSession,
    isLoadingSessions,
    enabled: syncRoute,
  })

  return {
    sessions: sessionsWithTitle,
    isLoadingSessions,
    currentSessionId,
    currentDropdownChatSession,
    deleteChatSession,
    deleteChatSessions,
    handleSelectSession,
    handleDropdownMenuOpen,
    selectedSessions,
    setSelectedSessions,
    showDeleteSessionAlertDialog,
    setShowDeleteSessionAlertDialog,
    showDeleteSessionsAlertDialog,
    setShowDeleteSessionsAlertDialog,
    deleteChatSessionMutation,
    deleteChatSessionsMutation,
    groupedFilteredSessionsByDate,
    filteredSessions,
    searchTerm,
    setSearchTerm,
    filteredFavoriteSessions,
    toggleFavoriteSession,
    isFavorited,
    selectMode,
    setSelectMode,
    startRenamingSession,
    cancelRenamingSession,
    renamingSessionId,
    renameChatSessionMutation,
    handleRenameSessionTitle,
  }
}
